import { useQuery } from "@tanstack/react-query";
import { pb } from "@/lib/pocketbase";
import type { UserScoreResponse } from "@/types/pocketbase-types";
import { useAuth } from "@/context/auth-context";

interface UserStats {
  totalPoints: number;
  booksRead: number;
  articlesRead: number;
  averageScore: number;
  currentStreak: number;
  totalTimeMinutes: number;
}

export interface UserActivity {
  id: string;
  title: string;
  type: "book" | "article";
  date: string;
  score: number;
  points: number;
  timeSpent: number;
  coverImage?: string;
}

export const useUserStats = () => {
  const { user } = useAuth();
  const enabled = !!user;
  return useQuery({
    queryKey: ["userStats", user?.id],
    queryFn: async (): Promise<UserStats> => {
      try {
        // Get user record with points_balance

        // Get all user scores for other statistics
        const scores = await pb
          .collection("user_score")
          .getList<UserScoreResponse>(1, 500, {
            filter: `user = "${user?.id}"`,
            sort: "-created",
          });

        // Use points_balance from user record instead of calculating sum
        const totalPoints = user?.points_balance || 0;
        const booksRead = scores.items.filter(
          (s: UserScoreResponse) => s.book
        ).length;
        const articlesRead = scores.items.filter(
          (s: UserScoreResponse) => s.article
        ).length;

        const averageScore =
          scores.items.length > 0
            ? Math.round(
                scores.items.reduce(
                  (sum: number, score: UserScoreResponse) =>
                    sum + (score.score || 0),
                  0
                ) / scores.items.length
              )
            : 0;

        // Calculate streak (simplified - in real app would need more complex logic)
        let currentStreak = 0;
        const today = new Date();
        const sortedScores = scores.items.sort(
          (a: UserScoreResponse, b: UserScoreResponse) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
        );

        for (let i = 0; i < sortedScores.length; i++) {
          const scoreDate = new Date(sortedScores[i].created);
          const daysDiff = Math.floor(
            (today.getTime() - scoreDate.getTime()) / (1000 * 60 * 60 * 24)
          );

          if (daysDiff === i) {
            currentStreak++;
          } else {
            break;
          }
        }

        // Calculate total time (mock - would need time tracking)
        const totalTimeMinutes = scores.items.length * 15; // Assume 15 min per activity

        return {
          totalPoints,
          booksRead,
          articlesRead,
          averageScore,
          currentStreak,
          totalTimeMinutes,
        };
      } catch (error) {
        console.error("Error fetching user stats:", error);
        return {
          totalPoints: 0,
          booksRead: 0,
          articlesRead: 0,
          averageScore: 0,
          currentStreak: 0,
          totalTimeMinutes: 0,
        };
      }
    },
    enabled,
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
  });
};

export interface UserActivitiesResult {
  activities: UserActivity[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export const useUserActivities = (page = 1, perPage = 10) => {
  const { user } = useAuth();
  const enabled = !!user;
  return useQuery<UserActivitiesResult>({
    queryKey: ["userActivities", user?.id, page, perPage],
    queryFn: async ({ client }): Promise<UserActivitiesResult> => {
      try {
        // Get user scores with expanded section data
        const scores = await pb
          .collection("user_score")
          .getList<UserScoreResponse>(page, perPage, {
            filter: `user = "${user?.id}"`,
            sort: "-created",
            expand: "article,book",
          });

        // Transform into activities
        const activities: UserActivity[] = scores.items.map(
          (score: UserScoreResponse) => {
            const isBook = !!score.book;
            // TypeScript doesn't know about expanded relations, so we need to cast
            const expand = score.expand as any;
            const expandedData = expand?.article || expand?.book;

            // Calculate percentage from correct_answers and total_questions
            const correctAnswers = score.correct_answers || 0;
            const totalQuestions = score.total_questions || 1; // Avoid division by zero
            const percentage = Math.round(
              (correctAnswers / totalQuestions) * 100
            );

            // Keep time_spent in seconds (backend stores in seconds)
            const timeSpentSeconds = score.time_spent || 0;

            return {
              id: score.id,
              title: expandedData?.title || "Unknown",
              type: isBook ? "book" : "article",
              date: score.created,
              score: percentage, // Use percentage instead of points
              points: score.score || 0, // Keep points as actual score
              timeSpent: timeSpentSeconds, // Use actual time spent in seconds
              coverImage: expandedData?.cover_image,
            };
          }
        );

        return {
          activities,
          totalItems: scores.totalItems,
          totalPages: scores.totalPages,
          currentPage: scores.page,
          hasNextPage: scores.page < scores.totalPages,
          hasPrevPage: scores.page > 1,
        };
      } catch (error) {
        console.error(
          "Error fetching user activities | returning the old data instead",
          error
        );
        return (
          client.getQueryData<UserActivitiesResult>([
            "userActivities",
            page,
            perPage,
          ]) ?? {
            activities: [],
            currentPage: 1,
            hasNextPage: false,
            hasPrevPage: false,
            totalItems: 0,
            totalPages: 0,
          }
        );
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUserAchievements = () => {
  
  const { user } = useAuth();
  const enabled = !!user;
  return useQuery({
    queryKey: ["userAchievements", user?.id],
    queryFn: async () => {
      try {
        // Get user record with points_balance

        // Get all user scores for achievement calculation
        const scores = await pb
          .collection("user_score")
          .getList<UserScoreResponse>(1, 500, {
            filter: `user = "${user?.id}"`,
            sort: "-created",
          });

        // Use points_balance from user record instead of calculating sum
        const totalPoints = user?.points_balance || 0;
        const booksRead = scores.items.filter(
          (s: UserScoreResponse) => s.book
        ).length;
        const articlesRead = scores.items.filter(
          (s: UserScoreResponse) => s.article
        ).length;

        // Calculate streak
        let currentStreak = 0;
        const today = new Date();
        const sortedScores = scores.items.sort(
          (a: UserScoreResponse, b: UserScoreResponse) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
        );

        for (let i = 0; i < sortedScores.length; i++) {
          const scoreDate = new Date(sortedScores[i].created);
          const daysDiff = Math.floor(
            (today.getTime() - scoreDate.getTime()) / (1000 * 60 * 60 * 24)
          );

          if (daysDiff === i) {
            currentStreak++;
          } else {
            break;
          }
        }

        // Calculate achievement progress based on stats
        return [
          {
            id: "first-steps",
            icon: "🏅",
            title: "First Steps",
            description: "Complete your first quiz",
            unlocked: booksRead + articlesRead > 0,
            progress: Math.min(booksRead + articlesRead, 1),
            maxProgress: 1,
            unlockedAt:
              booksRead + articlesRead > 0
                ? new Date().toISOString()
                : undefined,
          },
          {
            id: "bookworm",
            icon: "📖",
            title: "Bookworm",
            description: "Read 5 books",
            unlocked: booksRead >= 5,
            progress: booksRead,
            maxProgress: 5,
          },
          {
            id: "news-junkie",
            icon: "📰",
            title: "News Junkie",
            description: "Read 20 articles",
            unlocked: articlesRead >= 20,
            progress: articlesRead,
            maxProgress: 20,
          },
          {
            id: "perfect-score",
            icon: "💯",
            title: "Perfect Score",
            description: "Get 100% on any quiz",
            unlocked: false, // Would need to check individual scores
            progress: 0,
            maxProgress: 1,
          },
          {
            id: "week-warrior",
            icon: "🔥",
            title: "Week Warrior",
            description: "7-day reading streak",
            unlocked: currentStreak >= 7,
            progress: currentStreak,
            maxProgress: 7,
          },
          {
            id: "speed-reader",
            icon: "⚡",
            title: "Speed Reader",
            description: "Complete 10 quizzes",
            unlocked: booksRead + articlesRead >= 10,
            progress: booksRead + articlesRead,
            maxProgress: 10,
          },
          {
            id: "scholar",
            icon: "🎓",
            title: "Scholar",
            description: "Reach 1000 points",
            unlocked: totalPoints >= 1000,
            progress: totalPoints,
            maxProgress: 1000,
          },
          {
            id: "dedication",
            icon: "🏆",
            title: "Dedication",
            description: "30-day reading streak",
            unlocked: currentStreak >= 30,
            progress: currentStreak,
            maxProgress: 30,
          },
        ];
      } catch (error) {
        console.error("Error calculating achievements:", error);
        return [];
      }
    },
    enabled,
  });
};
